import { Application, Renderer, RenderTexture } from "pixi.js"

import { SoftenedGraphics } from "./rendering/impl/pixijs/softened_graphics"
import { contentBoundsReads } from "./rendering/impl/pixijs/sprites/texture_content_bounds"
import { StoreyFocusFilter } from "./rendering/impl/pixijs/storey_focus_filter"

// Find out what a slow frame was spent on, from the browser console, without a rebuild.
//
//     hitchProbe.record(20)            // record 20s, then print a report and download it as JSON
//     hitchProbe.record(20, "no fx")   // ...labelled, for an A/B against another recording
//     hitchProbe.start() / .stop()     // the same by hand; stop() returns the report
//
// The FPS meter says THAT a frame was slow; this says what was in it. Idle until started: start()
// wraps the frame's phases (the client update, the map, the storeys, the light mask, the stage
// render...) in timers, counts the GL work each frame hands the GPU (framebuffer binds, draws,
// texture uploads, shader compiles, sync readbacks), and notes the player's position, so a run of
// slow frames can be tied to a cause AND to the place it happened. stop() puts everything back.
//
// A frame's SPAN is from its start to the next frame's start: what the player sees as that frame's
// duration. Work anywhere in that span counts against it -- the frame's own JS, socket messages
// handled before the next frame, a GC, and GPU work the next frame had to wait out. The game is
// GPU-bound in the castle (multi-floor-fps-render-passes), so a slow frame with little JS is GPU
// work, and the GL counts are what tell one apart from a fast one.

/** One frame as recorded. Phases are inclusive ms (a phase includes the phases nested in it). */
export interface ProbeFrame {
    /** Frame start, ms since the recording began. */
    t: number
    /** This frame's start to the next frame's start (filled in at the next frame). */
    span: number
    /** Main-thread time from the frame's first ticker listener to the end of its last. */
    js: number
    phases: Record<string, number>
    counts: Record<string, number>
    events: string[]
    x: number
    y: number
    room?: string
    floor: number
    heapMb?: number
}

export interface ProbeContrast {
    metric: string
    slow: number
    normal: number
    /** slow - normal: in ms for a phase, in calls for a count. */
    delta: number
}

export interface ProbeSummary {
    frames: number
    seconds: number
    fps: number
    /** The typical span: the display's refresh when the game keeps up. */
    medianSpan: number
    /** Spans over this missed at least one refresh. */
    slowThreshold: number
    slowFrames: number
    slowPct: number
    spanPct: { p50: number; p90: number; p99: number; max: number }
    jsPct: { p50: number; p90: number; p99: number; max: number }
    /** Mean ms per frame for every phase, biggest first. */
    phases: { name: string; mean: number; p95: number; max: number }[]
    /** What differs between slow and normal frames, by how much it adds to a slow frame. */
    contrast: ProbeContrast[]
    /** Frames between consecutive slow frames, commonest first: a steady beat means something periodic. */
    beat: { every: number; times: number }[]
    /** Tiles where slow frames happened, worst share first (tiles with at least 8 frames). */
    tiles: { tile: string; frames: number; slow: number; share: number }[]
    worst: ProbeFrame[]
}

const percentile = (sorted: number[], p: number): number =>
    sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))] : 0

const round = (n: number, places = 2): number => {
    const scale = 10 ** places
    return Math.round(n * scale) / scale
}

const pct = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b)
    return {
        p50: round(percentile(sorted, 0.5)), p90: round(percentile(sorted, 0.9)),
        p99: round(percentile(sorted, 0.99)), max: round(sorted[sorted.length - 1] ?? 0),
    }
}

/** The report, from recorded frames. Pure, so it is tested without a renderer. */
export const summarizeProbe = (recorded: ProbeFrame[], worstCount = 25): ProbeSummary => {
    // The last frame has no next frame to close its span.
    const frames = recorded.filter(f => f.span > 0)
    const spans = frames.map(f => f.span).sort((a, b) => a - b)
    const medianSpan = percentile(spans, 0.5)
    // A quarter of a refresh past the typical frame: vsync puts a frame that missed at a whole extra refresh.
    const slowThreshold = medianSpan * 1.25
    const isSlow = (f: ProbeFrame) => f.span > slowThreshold
    const slow = frames.filter(isSlow)
    const normal = frames.filter(f => !isSlow(f))
    const total = frames.reduce((sum, f) => sum + f.span, 0)

    const phaseNames = new Set<string>()
    const countNames = new Set<string>()
    for (const f of frames) {
        Object.keys(f.phases).forEach(k => phaseNames.add(k))
        Object.keys(f.counts).forEach(k => countNames.add(k))
    }
    const mean = (set: ProbeFrame[], read: (f: ProbeFrame) => number) =>
        set.length ? set.reduce((sum, f) => sum + read(f), 0) / set.length : 0

    const phases = [...phaseNames].map(name => {
        const values = frames.map(f => f.phases[name] ?? 0).sort((a, b) => a - b)
        return { name, mean: round(mean(frames, f => f.phases[name] ?? 0), 3), p95: round(percentile(values, 0.95), 3), max: round(values[values.length - 1] ?? 0, 3) }
    }).sort((a, b) => b.mean - a.mean)

    const contrastOf = (metric: string, read: (f: ProbeFrame) => number): ProbeContrast => {
        const s = mean(slow, read)
        const n = mean(normal, read)
        return { metric, slow: round(s, 3), normal: round(n, 3), delta: round(s - n, 3) }
    }
    const contrast = [
        contrastOf("js", f => f.js),
        ...[...phaseNames].map(name => contrastOf(name, f => f.phases[name] ?? 0)),
        ...[...countNames].map(name => contrastOf(`#${name}`, f => f.counts[name] ?? 0)),
    ].filter(c => Math.abs(c.delta) > 0.001)
        .sort((a, b) => Math.abs(b.delta) / (Math.abs(b.normal) + 1) - Math.abs(a.delta) / (Math.abs(a.normal) + 1))

    const beats = new Map<number, number>()
    let last = -1
    frames.forEach((f, i) => {
        if (!isSlow(f)) return
        if (last >= 0) beats.set(i - last, (beats.get(i - last) ?? 0) + 1)
        last = i
    })
    const beat = [...beats].map(([every, times]) => ({ every, times })).sort((a, b) => b.times - a.times).slice(0, 8)

    const byTile = new Map<string, { frames: number; slow: number }>()
    for (const f of frames) {
        const tile = `${Math.floor(f.x / 32)},${Math.floor(f.y / 32)}${f.floor ? `@${f.floor}` : ""}`
        const entry = byTile.get(tile) ?? { frames: 0, slow: 0 }
        entry.frames++
        if (isSlow(f)) entry.slow++
        byTile.set(tile, entry)
    }
    const tiles = [...byTile].filter(([, e]) => e.frames >= 8)
        .map(([tile, e]) => ({ tile, frames: e.frames, slow: e.slow, share: round(e.slow / e.frames) }))
        .sort((a, b) => b.share - a.share)

    return {
        frames: frames.length,
        seconds: round(total / 1000, 1),
        fps: round(total ? (frames.length * 1000) / total : 0, 1),
        medianSpan: round(medianSpan),
        slowThreshold: round(slowThreshold),
        slowFrames: slow.length,
        slowPct: round(frames.length ? (100 * slow.length) / frames.length : 0, 1),
        spanPct: pct(frames.map(f => f.span)),
        jsPct: pct(frames.map(f => f.js)),
        phases,
        contrast,
        beat,
        tiles,
        worst: [...frames].sort((a, b) => b.span - a.span).slice(0, worstCount),
    }
}

/** The report as text, for the console. */
export const formatProbeSummary = (s: ProbeSummary, label?: string): string => {
    const lines: string[] = []
    lines.push(`hitch probe${label ? ` [${label}]` : ""}: ${s.frames} frames in ${s.seconds}s, ${s.fps} fps`)
    lines.push(`span ms p50 ${s.spanPct.p50} p90 ${s.spanPct.p90} p99 ${s.spanPct.p99} max ${s.spanPct.max}; ` +
        `slow (> ${s.slowThreshold}ms): ${s.slowFrames} (${s.slowPct}%)`)
    lines.push(`js ms p50 ${s.jsPct.p50} p90 ${s.jsPct.p90} p99 ${s.jsPct.p99} max ${s.jsPct.max}`)
    lines.push("", "phases (mean / p95 / max ms):")
    s.phases.slice(0, 24).forEach(p => lines.push(`  ${p.name.padEnd(34)} ${String(p.mean).padStart(7)} ${String(p.p95).padStart(7)} ${String(p.max).padStart(8)}`))
    lines.push("", "slow vs normal frames (mean per frame; # = calls):")
    s.contrast.slice(0, 24).forEach(c => lines.push(`  ${c.metric.padEnd(34)} ${String(c.slow).padStart(9)} vs ${String(c.normal).padStart(9)}`))
    lines.push("", `slow-frame beat (frames apart x times): ${s.beat.map(b => `${b.every}x${b.times}`).join("  ") || "none"}`)
    lines.push("", "worst tiles (tile: slow/frames):")
    s.tiles.slice(0, 12).forEach(t => lines.push(`  ${t.tile.padEnd(12)} ${t.slow}/${t.frames} (${Math.round(t.share * 100)}%)`))
    lines.push("", "worst frames:")
    s.worst.slice(0, 12).forEach(f => {
        const top = Object.entries(f.phases).filter(([, ms]) => ms >= 0.25).sort((a, b) => b[1] - a[1]).slice(0, 8)
            .map(([name, ms]) => `${name} ${round(ms, 1)}`).join(", ")
        const counts = Object.entries(f.counts).filter(([name]) => !name.startsWith("filter.")).map(([name, n]) => `${name}:${n}`).join(" ")
        lines.push(`  t=${round(f.t / 1000, 2)}s span ${round(f.span, 1)} js ${round(f.js, 1)} at ${Math.round(f.x)},${Math.round(f.y)}${f.floor ? `@${f.floor}` : ""}`)
        if (top) lines.push(`    ${top}`)
        lines.push(`    ${counts}`)
        if (f.events.length) lines.push(`    events: ${f.events.slice(0, 8).join("; ")}`)
    })
    return lines.join("\n")
}

// The GL calls counted every frame, under a short name.
const COUNTED_GL: Record<string, string> = {
    bindFramebuffer: "fb", drawElements: "draw", drawArrays: "draw", drawElementsInstanced: "draw", drawArraysInstanced: "draw",
    clear: "clear", useProgram: "program", bindTexture: "bindTex",
}

// GL calls that make the main thread wait for the GPU process (or are worth seeing when they happen at all): timed.
const TIMED_GL = ["readPixels", "getBufferSubData", "clientWaitSync", "getSyncParameter", "fenceSync", "finish", "getError",
    "getParameter", "getShaderParameter", "getProgramParameter", "checkFramebufferStatus", "compileShader", "linkProgram"]

const UPLOAD_GL = ["texImage2D", "texSubImage2D", "compressedTexImage2D", "texImage3D", "texSubImage3D"]
const BUFFER_GL = ["bufferData", "bufferSubData"]

// Big texture uploads and render targets get an event of their own: one is enough to drop a frame.
const NOTABLE_BYTES = 256 * 1024

const uploadSize = (name: string, args: any[]): [number, number] => {
    // texImage2D(target, level, internalformat, width, height, border, format, type, pixels)
    // texImage2D(target, level, internalformat, format, type, source)
    // texSubImage2D(target, level, x, y, width, height, format, type, pixels)
    // texSubImage2D(target, level, x, y, format, type, source)
    if (name === "texImage2D" || name === "compressedTexImage2D") {
        if (args.length >= 8 || name === "compressedTexImage2D") return [args[3] | 0, args[4] | 0]
        const source = args[5]
        return [source?.width | 0, source?.height | 0]
    }
    if (name === "texSubImage2D") {
        if (args.length >= 9) return [args[4] | 0, args[5] | 0]
        const source = args[6]
        return [source?.width | 0, source?.height | 0]
    }
    return [args[3] | 0, args[4] | 0]
}

interface Restorer { (): void }

// Ticker priorities that bracket every listener pixi and the game add (the stage render is LOW, -25).
const FIRST = 1000
const LAST = -1000

class HitchProbe {
    private frames: ProbeFrame[] = []
    private current?: ProbeFrame
    private frameStart = 0
    private origin = 0
    private restorers: Restorer[] = []
    private running = false
    private wrappedMap?: any
    private wrappedStoreys = new WeakSet<object>()
    private wrappedProviders = new WeakSet<object>()
    private boundsReadsAt = { ...contentBoundsReads }
    private label?: string
    private sceneAtStart?: Record<string, unknown>
    private lastHeap?: number
    private timer?: ReturnType<typeof setTimeout>
    // A listener that throws stops pixi's ticker for good (index.ts): the probe must never take the game down with it.
    private readonly begin = () => { try { this.beginFrame() } catch (error) { this.fail(error) } }
    private readonly end = () => { try { this.endFrame() } catch (error) { this.fail(error) } }
    private failed = false

    private fail(error: unknown): void {
        if (this.failed) return
        this.failed = true
        console.warn("[hitch probe] frame hook failed; recording what it can", error)
    }

    constructor(private readonly app: Application, private readonly client: any) {}

    record = (seconds = 20, label?: string): Promise<ProbeSummary> => {
        this.start(label)
        return new Promise(resolve => {
            this.timer = setTimeout(() => {
                const summary = this.stop()
                this.save(summary)
                resolve(summary)
            }, seconds * 1000)
        })
    }

    start = (label?: string): void => {
        if (this.running) this.stop()
        this.running = true
        this.label = label
        this.frames = []
        this.current = undefined
        this.origin = performance.now()
        this.lastHeap = undefined
        this.sceneAtStart = this.scene()
        this.boundsReadsAt = { ...contentBoundsReads }
        this.wrappedStoreys = new WeakSet()
        this.wrappedProviders = new WeakSet()
        this.app.ticker.add(this.begin, undefined, FIRST)
        this.app.ticker.add(this.end, undefined, LAST)
        this.restorers.push(() => {
            this.app.ticker.remove(this.begin)
            this.app.ticker.remove(this.end)
        })
        this.wrapRenderer()
        this.wrapGl()
        this.wrapClient()
        this.wrapPrototypes()
        console.log(`[hitch probe] recording${label ? ` "${label}"` : ""}... hitchProbe.stop() for the report`)
    }

    stop = (): ProbeSummary => {
        if (this.timer) clearTimeout(this.timer)
        this.timer = undefined
        this.running = false
        // Undo in reverse, so a method wrapped twice (a prototype and its instance) comes back as it was.
        this.restorers.reverse().forEach(restore => {
            try { restore() } catch (error) { console.warn("[hitch probe] restore failed", error) }
        })
        this.restorers = []
        this.wrappedMap = undefined
        const summary = summarizeProbe(this.frames)
        console.log(formatProbeSummary(summary, this.label))
        return summary
    }

    /**
     * What drawing the scene costs, layer by layer: `await hitchProbe.drawCost()` while standing somewhere typical.
     * Pauses the game, draws the stage back to back (each batch ended by a one-pixel readback, so the time is the
     * GPU's and not the refresh rate's), then again with each named layer hidden, each filter dropped and each mask
     * lifted in turn. A layer's `saves` is what it costs to draw. Prints a table and returns the rows.
     */
    drawCost = async (renders = 12, only?: string[], pairs = 5): Promise<{ label: string; ms: number; saves: number }[]> => {
        const app = this.app, renderer = this.app.renderer as Renderer, gl = renderer.gl as WebGLRenderingContext
        const pixel = new Uint8Array(4)
        const draw = () => renderer.render(app.stage)
        const sync = () => gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel)
        const once = () => {
            draw(); sync()
            const t0 = performance.now()
            for (let i = 0; i < renders; i++) draw()
            sync()
            return (performance.now() - t0) / renders
        }
        const measure = () => {
            const runs = [once(), once(), once()].sort((a, b) => a - b)
            return runs[1]
        }
        const names = this.layerNames()
        const label = (node: any, path: string) => names.get(node) ?? `${path} ${node.constructor?.name ?? "?"}${node.children?.length ? `(${node.children.length})` : ""}`
        // The layers worth measuring: every named layer wherever it is, and the containers (not single sprites: a
        // castle's map holds thousands) two deep. Named ones first; at most MAX of them.
        const MAX = 60
        const named: { node: any; path: string }[] = [], unnamed: { node: any; path: string }[] = []
        const walk = (node: any, path: string, depth: number) => {
            const children: any[] = node.children ?? []
            // A crowd (a map's thousands of sprites): only its named layers, and no further down the unnamed.
            const crowd = children.length > 200
            children.forEach((child: any, i: number) => {
                const at = `${path}/${i}`
                if (!child.visible || child.worldAlpha === 0) return
                if (names.has(child)) named.push({ node: child, path: at })
                else if (crowd) return
                else if (depth < 3 && child.children?.length) unnamed.push({ node: child, path: at })
                if (depth < 5) walk(child, at, depth + 1)
            })
        }
        walk(app.stage, "", 1)
        const candidates = [...named, ...unnamed].filter(({ node, path }) => !only || only.some(text => label(node, path).includes(text))).slice(0, MAX)
        const deadline = performance.now() + 60_000
        app.ticker.stop()
        try {
            const base = measure()
            const rows: { label: string; ms: number; saves: number }[] = [{ label: "everything", ms: base, saves: 0 }]
            // Interleaved: the baseline is measured again beside each layer, and the saving is the median of the pairs'
            // differences, so drift (the GPU's clocks, another tab) cancels out rather than landing on whichever layer
            // happened to be measured during it.
            const without = (text: string, apply: () => () => void) => {
                const diffs: number[] = [], hidden: number[] = []
                for (let pair = 0; pair < pairs; pair++) {
                    const before = once()
                    const restore = apply()
                    let ms: number
                    try { ms = once() } finally { restore() }
                    const after = once()
                    hidden.push(ms)
                    diffs.push((before + after) / 2 - ms)
                }
                diffs.sort((a, b) => a - b)
                hidden.sort((a, b) => a - b)
                rows.push({ label: text, ms: hidden[pairs >> 1], saves: diffs[pairs >> 1] })
            }
            for (const { node, path } of candidates) {
                if (performance.now() > deadline) break
                // Let the page breathe between layers (the game itself stays paused).
                await new Promise(resolve => setTimeout(resolve, 0))
                without(`hide ${label(node, path)}`, () => { node.visible = false; return () => { node.visible = true } })
                if (node.filters?.length) {
                    without(`no filters on ${label(node, path)} [${node.filters.map((f: any) => f?.constructor?.name).join(",")}]`,
                        () => { const was = node.filters; node.filters = null; return () => { node.filters = was } })
                }
                if (node.mask) without(`no mask on ${label(node, path)}`, () => { const was = node.mask; node.mask = null; return () => { node.mask = was } })
            }
            rows.sort((a, b) => b.saves - a.saves)
            const shown = rows.filter(row => row.label === "everything" || row.saves > 0.05)
            console.table(shown.map(row => ({ layer: row.label, ms: round(row.ms, 2), saves: round(row.saves, 2) })))
            return rows.map(row => ({ ...row, ms: round(row.ms, 3), saves: round(row.saves, 3) }))
        } finally {
            app.ticker.start()
        }
    }

    /** The display objects the game keeps by name (its renderers' fields), so a cost table can say what they are. */
    private layerNames(): Map<object, string> {
        const names = new Map<object, string>()
        const isDisplay = (value: any) => !!value && typeof value === "object" && "worldTransform" in value && typeof value.render === "function"
        const scan = (owner: any, prefix: string) => {
            if (!owner) return
            for (const key of Object.keys(owner)) {
                let value: any
                try { value = owner[key] } catch { continue }
                if (isDisplay(value) && !names.has(value)) names.set(value, `${prefix}.${key}`)
            }
        }
        const renderer = this.client.clientGameLogic?.clientRenderer
        scan(renderer, "renderer")
        const map = renderer?.map?.()
        scan(map, "map")
        scan(renderer?.entity?.(), "entities")
        scan(renderer?.gui?.(), "gui")
        scan(renderer?.shadowRendering, "light")
        const floors: Map<string, any> | undefined = map?.sceneRenderer?.floors
        floors?.forEach((floor: any) => {
            const f = floor?.provider?.twoStoreyHouse?.floor ?? "?"
            if (floor?.root) names.set(floor.root, `storey ${f}`)
            scan(floor?.map, `storey ${f} map`)
        })
        return names
    }

    /** Downloads the report and every frame as JSON (to ~/Downloads in a desktop browser). */
    save = (summary: ProbeSummary = summarizeProbe(this.frames)): void => {
        const renderer = this.app.renderer as Renderer
        const w = window as any
        const toggles: Record<string, unknown> = {}
        for (const key of ["buildingShadow", "foliageShadow", "spriteShadow", "storeyFocus", "storeyEdges", "storeyTone", "indoorSight", "occluderFade", "occlusionOutline", "nightFog"]) {
            const value = w[key]
            toggles[key] = value === undefined ? "default" : value === false ? false : typeof value === "object" ? (value.enabled ?? "on") : value
        }
        const report = {
            label: this.label,
            at: new Date().toISOString(),
            userAgent: navigator.userAgent,
            devicePixelRatio: window.devicePixelRatio,
            canvas: { width: renderer.view.width, height: renderer.view.height, resolution: renderer.resolution },
            toggles,
            sceneAtStart: this.sceneAtStart,
            sceneAtEnd: this.scene(),
            summary,
            frames: this.frames.filter(f => f.span > 0),
        }
        const blob = new Blob([JSON.stringify(report)], { type: "application/json" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        const stamp = report.at.replace(/[:.]/g, "-")
        link.download = `hitch-probe-${this.label ? `${this.label.replace(/[^a-z0-9-]+/gi, "_")}-` : ""}${stamp}.json`
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(link.href), 10_000)
        console.log(`[hitch probe] saved ${link.download}`)
    }

    private beginFrame(): void {
        const now = performance.now()
        if (this.current) {
            this.current.span = now - this.frameStart
            // Texture bounds measured during that span: from images on the CPU, and read back from the GPU.
            const reads = contentBoundsReads, was = this.boundsReadsAt
            if (reads.image > was.image) {
                this.current.counts["bounds.image"] = reads.image - was.image
                this.current.phases["bounds.image"] = reads.imageMs - was.imageMs
            }
            if (reads.gpu > was.gpu) this.current.counts["bounds.gpu"] = reads.gpu - was.gpu
            this.boundsReadsAt = { ...reads }
            this.frames.push(this.current)
        }
        this.frameStart = now
        const logic = this.client.clientGameLogic
        const player = logic?.playerEntity
        const map = logic?.clientRenderer?.map?.()
        const frame: ProbeFrame = {
            t: now - this.origin, span: 0, js: 0, phases: {}, counts: {}, events: [],
            x: player?.location?.x ?? 0, y: player?.location?.y ?? 0, room: player?.roomId,
            floor: map?.tilemapMetaProvider?.twoStoreyHouse?.floor ?? 0,
        }
        const heap = (performance as any).memory?.usedJSHeapSize
        if (typeof heap === "number") {
            frame.heapMb = round(heap / 1048576, 1)
            // The heap only shrinks when the collector runs: a drop is a GC somewhere in the last span.
            if (this.lastHeap !== undefined && this.lastHeap - heap > 1048576 && this.frames.length) {
                this.frames[this.frames.length - 1].events.push(`gc -${round((this.lastHeap - heap) / 1048576, 1)}MB`)
            }
            this.lastHeap = heap
        }
        this.current = frame
        // The map is replaced on a storey hand-off: follow it.
        if (map && map !== this.wrappedMap) {
            if (this.wrappedMap) frame.events.push("map hand-off")
            this.wrapMap(map)
        }
        if (map) this.wrapStoreys(map)
    }

    private endFrame(): void {
        if (this.current) this.current.js = performance.now() - this.frameStart
    }

    private add(phase: string, ms: number): void {
        const frame = this.current
        // The clock is coarsened to 0.1ms outside a cross-origin-isolated page: most calls read 0, and need no entry.
        if (!frame || ms <= 0) return
        frame.phases[phase] = (frame.phases[phase] ?? 0) + ms
    }

    private count(name: string, n = 1): void {
        const frame = this.current
        if (!frame) return
        frame.counts[name] = (frame.counts[name] ?? 0) + n
    }

    private event(text: string): void {
        this.current?.events.push(text)
    }

    /** The local player's sprite (a composite whose `animations` hold its baked frames), if drawn. */
    private playerSprite(): any {
        const logic = this.client.clientGameLogic
        const player = logic?.playerEntity
        return player && logic?.clientRenderer?.entity?.()?.get?.(player)?.sprite
    }

    /** What a texture is: one of the player's poses (and which), or its kind, size and cache id. */
    private describeTexture(texture: any): string {
        const sprite = this.playerSprite()
        for (const [action, result] of Object.entries<any>(sprite?.animations ?? {})) {
            if (result?.animation?.textures?.includes(texture) || result?.sprite?.texture === texture) {
                return `player pose ${action} (${sprite.constructor?.name})`
            }
        }
        const base = texture.baseTexture
        const kind = texture instanceof RenderTexture ? "RenderTexture" : base?.resource?.constructor?.name ?? "?"
        const id = texture.textureCacheIds?.[0] ?? base?.textureCacheIds?.[0] ?? ""
        return `${kind} ${Math.round(texture.width ?? 0)}x${Math.round(texture.height ?? 0)}${id ? ` ${String(id).slice(-48)}` : ""}`
    }

    private scene(): Record<string, unknown> {
        const logic = this.client.clientGameLogic
        const sprite = this.playerSprite()
        return {
            brightness: logic?.currentBrightness,
            playerSprite: sprite?.constructor?.name,
            spriteName: sprite?.spriteName,
            appearance: logic?.playerEntity?.appearance,
        }
    }

    /** Times every call of obj[name] as `label`, and counts it. */
    private wrap<T = unknown>(obj: any, name: string, label: string, after?: (args: any[], ms: number, before: T) => void,
        before?: () => T): void {
        if (!obj) return
        const original = obj[name]
        if (typeof original !== "function" || original.__hitchProbe) return
        const own = Object.prototype.hasOwnProperty.call(obj, name)
        const probe = this
        const wrapped = function (this: any, ...args: any[]) {
            const state = before?.() as T
            const t0 = performance.now()
            try {
                return original.apply(this, args)
            } finally {
                const ms = performance.now() - t0
                probe.add(label, ms)
                probe.count(label)
                after?.(args, ms, state)
            }
        }
        ;(wrapped as any).__hitchProbe = true
        obj[name] = wrapped
        this.restorers.push(() => {
            if (own) obj[name] = original
            else delete obj[name]
        })
    }

    private wrapRenderer(): void {
        const renderer = this.app.renderer as any
        const probe = this
        const render = renderer.render
        renderer.render = function (displayObject: any, options?: any, ...rest: any[]) {
            const target: RenderTexture | undefined = options instanceof RenderTexture ? options : options?.renderTexture
            const t0 = performance.now()
            try {
                return render.call(this, displayObject, options, ...rest)
            } finally {
                const ms = performance.now() - t0
                const label = target ? "rt.render" : "stage.render"
                probe.add(label, ms)
                probe.count(label)
                if (target && target.width * target.height * renderer.resolution ** 2 * 4 >= NOTABLE_BYTES && ms > 1) {
                    probe.event(`rt.render ${Math.round(target.width)}x${Math.round(target.height)} ${round(ms, 1)}ms`)
                }
            }
        }
        this.restorers.push(() => { renderer.render = render })

        this.wrap(renderer, "generateTexture", "generateTexture", (args, ms) => {
            // A lone sprite is a texture being copied out to be read (texture_content_bounds): say whose.
            const texture = args[0]?.texture
            if (texture && !args[0].children?.length) {
                this.event(`generateTexture of ${this.describeTexture(texture)} ${round(ms, 1)}ms`)
                return
            }
            if (ms <= 0.5) return
            const region = args[1]?.region ?? args[3] ?? args[0]?.getLocalBounds?.()
            this.event(`generateTexture ${Math.round(region?.width ?? 0)}x${Math.round(region?.height ?? 0)} ${round(ms, 1)}ms`)
        })

        const filterSystem = renderer.filter
        const push = filterSystem.push
        filterSystem.push = function (target: any, filters: any[]) {
            probe.count("filter.push")
            for (const filter of filters ?? []) probe.count(`filter.${filter?.constructor?.name ?? "?"}`)
            return push.apply(this, arguments as any)
        }
        this.restorers.push(() => { filterSystem.push = push })
    }

    private wrapGl(): void {
        const gl = (this.app.renderer as Renderer).gl as any
        if (!gl) return
        const probe = this
        const own = (name: string) => {
            const original = gl[name]
            if (typeof original !== "function") return undefined
            this.restorers.push(() => { delete gl[name] })
            return original
        }
        for (const [name, short] of Object.entries(COUNTED_GL)) {
            const original = own(name)
            if (!original) continue
            gl[name] = function () {
                probe.count(short)
                return original.apply(gl, arguments)
            }
        }
        for (const name of TIMED_GL) {
            const original = own(name)
            if (!original) continue
            gl[name] = function () {
                const t0 = performance.now()
                try {
                    return original.apply(gl, arguments)
                } finally {
                    const ms = performance.now() - t0
                    probe.add(`gl.${name}`, ms)
                    probe.count(`gl.${name}`)
                    if (name === "compileShader" || name === "linkProgram" || ms > 2) probe.event(`gl.${name} ${round(ms, 1)}ms`)
                }
            }
        }
        for (const name of UPLOAD_GL) {
            const original = own(name)
            if (!original) continue
            gl[name] = function (...args: any[]) {
                const t0 = performance.now()
                try {
                    return original.apply(gl, args)
                } finally {
                    const ms = performance.now() - t0
                    const [w, h] = uploadSize(name, args)
                    probe.count("upload")
                    probe.count("uploadKB", Math.round((w * h * 4) / 1024))
                    probe.add("gl.upload", ms)
                    if (w * h * 4 >= NOTABLE_BYTES) probe.event(`${name} ${w}x${h} ${round(ms, 1)}ms`)
                }
            }
        }
        for (const name of BUFFER_GL) {
            const original = own(name)
            if (!original) continue
            gl[name] = function (...args: any[]) {
                const data = args[name === "bufferData" ? 1 : 2]
                const bytes = typeof data === "number" ? data : data?.byteLength ?? 0
                probe.count("bufferKB", Math.round(bytes / 1024))
                return original.apply(gl, args)
            }
        }
    }

    private wrapClient(): void {
        const client = this.client
        this.wrap(client, "update", "client.update")
        this.wrap(client, "processServerMessages", "client.processServerMessages")
        this.wrap(client, "processInputs", "client.processInputs")
        this.wrap(client, "interpolateEntities", "client.interpolateEntities")
        this.wrap(client, "renderWorld", "client.renderWorld")

        // Socket messages are parsed as they arrive, between frames.
        const ws = client.ws
        if (ws && typeof ws.onmessage === "function") {
            const onmessage = ws.onmessage
            const probe = this
            ws.onmessage = function (message: MessageEvent) {
                const t0 = performance.now()
                try {
                    return onmessage.call(this, message)
                } finally {
                    probe.add("net.onmessage", performance.now() - t0)
                    probe.count("net.messages")
                    probe.count("net.KB", Math.round((typeof message.data === "string" ? message.data.length : 0) / 1024))
                }
            }
            this.restorers.push(() => { if (ws.onmessage !== onmessage) ws.onmessage = onmessage })
        }

        const renderer = client.clientGameLogic?.clientRenderer
        if (!renderer) return
        this.wrap(renderer, "render", "renderer.render")
        this.wrap(renderer, "renderEntitiesOptimized", "renderer.entities")
        this.wrap(renderer, "updateFollowCam", "renderer.followCam")
        this.wrap(renderer.shadowRendering, "renderVisibility", "lightMask.renderVisibility")
        const entity = renderer.entity?.()
        this.wrap(entity, "render", "entity.render")
        this.wrap(entity, "update", "entity.update")
        this.wrap(entity, "beginFrame", "entity.beginFrame")
        this.wrap(entity, "endFrame", "entity.endFrame")
        this.wrap(entity, "updatePlayerOcclusionOutline", "entity.occlusionOutline")
        this.wrap(entity, "updateInteractionFocus", "entity.interactionFocus")
        // The ambient effects and overlays, whatever this build has: every `_name` field with an update().
        for (const key of Object.keys(renderer)) {
            const field = renderer[key]
            if (!key.startsWith("_") || !field || typeof field !== "object") continue
            if (typeof field.update === "function" && key !== "_map" && key !== "_entity" && key !== "_gui") this.wrap(field, "update", `fx.${key.slice(1)}`)
        }
        const overlay = renderer._worldOverlay
        if (overlay) {
            for (const name of ["animateHoldTimers", "animateRescueHints", "animateBonecallerWarnings", "animateDroppedItemGlints"]) {
                this.wrap(overlay, name, `overlay.${name}`)
            }
        }
    }

    /**
     * Times a map's sun-shadow update and, when it re-cast the shadows, says why: which part of its cache key changed
     * (a storey's walls are `room:version`; the light is `x:y`), or that it was looking again for plant art still loading.
     */
    private wrapShadow(map: any, label: string): void {
        this.wrap(map, "updateBuildingShadow", label, (_args, ms, before: { version?: string; retry: boolean }) => {
            const after: string | undefined = map.checkedShadowVersion
            if (after === before.version && !before.retry) return
            const was = String(before.version ?? "").split("|"), now = String(after ?? "").split("|")
            const changed = now.map((part, i) => part === was[i] ? undefined : `${was[i] ?? "-"} -> ${part}`).filter(Boolean)
            const why = before.retry ? "plant art retry" : before.version === undefined ? "first cast" : changed.join(", ")
            this.event(`shadow recast ${map.tilemapMetaProvider?.roomId ?? "?"} ${round(ms, 1)}ms: ${why}`)
        }, () => ({
            version: map.checkedShadowVersion,
            retry: map.foliageRetryAt !== undefined && performance.now() >= map.foliageRetryAt,
        }))
    }

    /** Notes every change to a storey's tile data -- what moves its presentation version -- and what made it. */
    private wrapProvider(provider: any): void {
        if (!provider || this.wrappedProviders.has(provider)) return
        this.wrappedProviders.add(provider)
        const room = String(provider.roomId ?? "?").slice(-12)
        for (const name of ["addTileAt", "removeTileAt", "updateBlockingAt", "setMapDeltaHealth"]) {
            this.wrap(provider, name, `tiles.${name}`, (args, _ms, caller: string) => {
                const [x, y] = args
                const tile = name === "addTileAt" ? ` ${args[4]}@${args[5]}` : name === "removeTileAt" ? ` @${args[2]}` : ""
                const hp = name === "addTileAt" && args[9]?.hp !== undefined ? ` hp ${args[9].hp}/${args[9].hpMax}` : name === "setMapDeltaHealth" ? ` hp ${args[1]}/${args[2]}` : ""
                this.event(`${name} ${room} ${x?.x ?? x},${x?.y ?? y}${tile}${hp} <- ${caller}`)
            }, () => (new Error().stack ?? "").split("\n").slice(3, 7)
                .map(line => line.trim().replace(/^at /, "").replace(/\s*\(.*$/, "")).join(" < "))
        }
    }

    /** The other storeys' maps, drawn inside the scene render: their own shadow updates and roof overlaps. */
    private wrapStoreys(map: any): void {
        const floors: Map<string, any> | undefined = map.sceneRenderer?.floors
        if (!floors) return
        for (const floor of floors.values()) {
            const storey = floor?.map
            if (!storey || storey === map || this.wrappedStoreys.has(storey)) continue
            this.wrappedStoreys.add(storey)
            this.wrapProvider(storey.tilemapMetaProvider)
            this.wrapShadow(storey, "storey.updateBuildingShadow")
            this.wrap(storey, "renderTileMap", "storey.renderTileMap")
            this.wrap(storey, "renderAllLayers", "storey.renderAllLayers", (_args, ms) => this.event(`storey full render ${round(ms, 1)}ms`))
            this.wrap(storey.roofCompositeOverlap, "rebuild", "storey.roofOverlap.rebuild", (_args, ms) => { if (ms > 1) this.event(`storey roofOverlap.rebuild ${round(ms, 1)}ms`) })
        }
    }

    private wrapMap(map: any): void {
        this.wrappedMap = map
        this.wrapProvider(map.tilemapMetaProvider)
        this.wrapShadow(map, "map.updateBuildingShadow")
        for (const name of ["renderTileMap", "processPendingDeltas", "repaintDirtyCells", "applyOccluderFade",
            "updateTileEffects", "updateLandings", "syncSceneComposites", "rebuildRoofComposites", "applyLadderVeils",
            "syncDownstairsVisibility", "updateHatches", "artworkSignature"]) {
            this.wrap(map, name, `map.${name}`)
        }
        this.wrap(map, "renderAllLayers", "map.renderAllLayers", (_args, ms) => this.event(`map full render ${round(ms, 1)}ms`))
        this.wrap(map, "rerender", "map.rerender", () => this.event("map.rerender"))
        this.wrap(map.sceneRenderer, "render", "map.scene.render")
        this.wrap(map.roofCompositeOverlap, "sync", "map.roofOverlap.sync")
        this.wrap(map.roofCompositeOverlap, "rebuild", "map.roofOverlap.rebuild", (_args, ms) => this.event(`roofOverlap.rebuild ${round(ms, 1)}ms`))
        this.wrap(map.storeyEdges, "sync", "map.storeyEdges.sync")
        this.wrap(map.groundRise, "tick", "map.groundRise.tick")
        this.wrap(map.water, "tick", "map.water.tick")
        this.wrap(map.rockMass, "tick", "map.rockMass.tick")
        this.wrap(map.skyManager, "updatePlayerLocation", "map.sky.updatePlayerLocation")
    }

    private wrapPrototypes(): void {
        // A storey above the player re-blurring its picture (its snapshot went stale), vs drawing the one it kept.
        this.wrap(StoreyFocusFilter.prototype, "apply", "focus.blur")
        this.wrap(StoreyFocusFilter.prototype, "drawSnapshot", "focus.snapshot")
        // A softened shadow layer baking its blur.
        this.wrap(SoftenedGraphics.prototype, "bake", "softened.bake", (_args, ms) => { if (ms > 0.5) this.event(`softened.bake ${round(ms, 1)}ms`) })
    }
}

export const installHitchProbe = (app: Application, client: unknown): void => {
    if (typeof window === "undefined") return
    ;(window as any).hitchProbe = new HitchProbe(app, client)
}
